import { useCallback, useEffect, useMemo, useState } from 'react';
import { listStreams } from '../api';
import { YouTubeLiveStream } from '../types';

export function useStreamsData() {
  const [streams, setStreams] = useState<YouTubeLiveStream[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [outputTargetFilter, setOutputTargetFilter] = useState<string>('all');
  const [dateFilter, setDateFilter] = useState<any>(null);
  // Archived streams are hidden unless the operator asks to see them.
  const [showArchived, setShowArchived] = useState(false);
  const [pageSize, setPageSize] = useState(10);

  const loadStreams = useCallback(async () => {
    setLoading(true);
    try {
      const data = await listStreams();
      setStreams(data);
      return data;
    } finally {
      setLoading(false);
    }
  }, []);

  // Auto-refresh streams every 5 minutes to keep monitoring data current.
  // Skip while the tab is hidden so background tabs don't keep polling.
  useEffect(() => {
    const interval = setInterval(() => {
      if (document.hidden) return;
      void loadStreams();
    }, 300_000);
    return () => clearInterval(interval);
  }, [loadStreams]);

  const filteredStreams = useMemo(() => {
    return streams.filter(stream => {
      const matchesSearch =
        !searchText ||
        stream.name.toLowerCase().includes(searchText.toLowerCase()) ||
        stream.description?.toLowerCase().includes(searchText.toLowerCase());

      const matchesStatus = statusFilter === 'all' || stream.status === statusFilter;

      const streamOutputTarget = stream.output_target || 'youtube';
      const matchesOutputTarget =
        outputTargetFilter === 'all' || streamOutputTarget === outputTargetFilter;

      const matchesDate =
        !dateFilter ||
        (stream.scheduled_start_time &&
          new Date(stream.scheduled_start_time).toDateString() === dateFilter.toDateString());

      const matchesArchived = showArchived || !stream.archived_at;

      return matchesSearch && matchesStatus && matchesOutputTarget && matchesDate && matchesArchived;
    });
  }, [streams, searchText, statusFilter, outputTargetFilter, dateFilter, showArchived]);

  return {
    streams,
    setStreams,
    filteredStreams,
    loading,
    loadStreams,
    searchText,
    setSearchText,
    statusFilter,
    setStatusFilter,
    outputTargetFilter,
    setOutputTargetFilter,
    dateFilter,
    setDateFilter,
    showArchived,
    setShowArchived,
    pageSize,
    setPageSize
  };
}
